/**
 * @file Handles request polling
 * */
import { fetchJson } from '@@/surface/api_fetch'
import type { FetchOptions } from '@padlet/fetch'
import { HTTPMethod } from '@padlet/fetch'

interface PollingOptions {
  intervalSecs: number
  maxAttempts: number
  method?: HTTPMethod
  fetchOptions?: FetchOptions
  // Optional exponential backoff. When `backoffFactor` is set, the delay before
  // attempt N is `min(intervalSecs * backoffFactor^(N-1), maxIntervalSecs)` — so
  // `intervalSecs` is the first wait and each subsequent one multiplies by
  // `backoffFactor`, capped at `maxIntervalSecs`. Omit for a fixed interval.
  backoffFactor?: number
  maxIntervalSecs?: number
}

interface PollingArguments<R> {
  pollingUrl: string
  validationCallback?: (response: R) => boolean
  onIteration?: (response: R) => void
  nextPollingUrl?: (response: R) => string
  options: PollingOptions
}

class PollTimeoutError extends Error {
  constructor(message: string = 'Polling timeout: max attempts reached') {
    super(message)
    this.name = 'PollTimeoutError'
  }
}

const defaultValidationCallback = (response): boolean => response.done

const poll = async <R = any>({
  pollingUrl,
  validationCallback,
  onIteration,
  nextPollingUrl,
  options,
}: PollingArguments<R>): Promise<any> => {
  let attempts = 0
  let url = pollingUrl
  const executePoll = async (resolve: (v: R) => void, reject: (e?: unknown) => void): Promise<void> => {
    try {
      const fetchOptions = options.fetchOptions ?? {}
      const response: R = await fetchJson(url, {
        method: options.method ?? HTTPMethod.post,
        ...fetchOptions,
      })
      attempts++

      if (onIteration) {
        onIteration(response)
      }

      if (validationCallback != null ? validationCallback(response) : defaultValidationCallback(response)) {
        return resolve(response)
      } else if (attempts >= options.maxAttempts) {
        return reject(new PollTimeoutError())
      } else {
        if (nextPollingUrl != null) url = nextPollingUrl(response)
        const delaySecs =
          options.backoffFactor != null
            ? Math.min(
                options.intervalSecs * options.backoffFactor ** (attempts - 1),
                options.maxIntervalSecs ?? Infinity,
              )
            : options.intervalSecs
        setTimeout(executePoll, delaySecs * 1000, resolve, reject)
      }
    } catch (validationError) {
      return reject(validationError)
    }
  }
  return await new Promise(executePoll)
}

export { poll, PollTimeoutError }
